import { isDoublesCategory, isTournamentDateReached } from '../constants/tournaments';

/**
 * Shared helpers for a member's tournament registrations / printable receipts.
 */

function canRequestPartnerChange(reg) {
  if (!reg) return false;
  if (reg.partner_change_requested) return false;
  if (reg.transfer_requested) return false;
  if (isTournamentDateReached(reg)) return false;
  if (reg.can_request_partner_change) return true;
  const st = String(reg.status || '').toLowerCase();
  if (!['pending', 'approved'].includes(st)) return false;
  const tStatus = String(reg.tournament_status || '').toLowerCase();
  if (['completed', 'cancelled'].includes(tStatus)) return false;
  if (!isDoublesCategory(reg.category)) return false;
  return true;
}

function canRequestTransfer(reg) {
  if (!reg) return false;
  if (reg.transfer_requested) return false;
  const st = String(reg.status || '').toLowerCase();
  if (st === 'withdrawal_requested') return false;
  if (reg.can_request_transfer) return true;
  if (!['pending', 'approved'].includes(st)) return false;
  const tStatus = String(reg.tournament_status || '').toLowerCase();
  if (['completed', 'cancelled'].includes(tStatus)) return false;
  return Boolean(reg.payment_verified);
}

/** Display label: title · category · skill */
export function formatRegistrationEventLabel(reg) {
  const title = String(reg?.title || '').trim();
  const category = String(reg?.category_label || '').trim();
  const skill = String(reg?.skill_level || '').trim();
  const bits = [];
  if (title) bits.push(title);
  if (category && category !== title) bits.push(category);
  if (skill) bits.push(skill);
  return bits.join(' · ') || 'another event';
}

/** Other confirmed upcoming regs on this account (not this event, not withdrawn/unpaid). */
export function otherActiveRegistrations(list, tournamentId) {
  const tid = String(tournamentId || '');
  return (Array.isArray(list) ? list : []).filter((r) => {
    if (!r) return false;
    if (tid && String(r.tournament_id || '') === tid) return false;
    const tStatus = String(r.tournament_status || '').toLowerCase();
    if (['completed', 'cancelled'].includes(tStatus)) return false;
    return isConfirmedTournamentRegistration(r);
  });
}

function proposedPartnerSummary(reg) {
  const proposed = reg?.partner_change_proposed && typeof reg.partner_change_proposed === 'object'
    ? reg.partner_change_proposed
    : null;
  const name = String(proposed?.partner_full_name || '').trim();
  if (!name) return '';
  const bits = [name];
  if (proposed.partner_gender) bits.push(proposed.partner_gender);
  if (proposed.partner_age != null && proposed.partner_age !== '') bits.push(`age ${proposed.partner_age}`);
  return bits.join(' · ');
}

function previousPartnerName(reg) {
  const prev = reg?.partner_change_previous && typeof reg.partner_change_previous === 'object'
    ? reg.partner_change_previous
    : null;
  return String(prev?.partner_full_name || '').trim();
}

/** Paid / admin-confirmed and still in the field (including a pending withdraw request). */
export const isConfirmedTournamentRegistration = (r) => {
  if (!r) return false;
  const st = String(r.status || '').toLowerCase();
  if (['refunded', 'rejected', 'withdrawn'].includes(st)) return false;
  if (['pending_payment', 'payment_failed', 'cancelled', 'expired'].includes(st)) {
    return Boolean(r.payment_verified);
  }
  return Boolean(r.payment_verified)
    || st === 'pending'
    || st === 'approved'
    || st === 'withdrawal_requested';
};

/** Joined this event — includes withdrawn so the player can see refund status. */
export const isJoinedTournamentRegistration = (r) => {
  if (!r) return false;
  const st = String(r.status || '').toLowerCase();
  if (['withdrawn', 'refunded'].includes(st)) return Boolean(r.payment_verified || r.refund_id);
  return isConfirmedTournamentRegistration(r);
};

export const filterConfirmedTournamentRegistrations = (list) =>
  (Array.isArray(list) ? list : []).filter(isConfirmedTournamentRegistration);

export const filterJoinedTournamentRegistrations = (list) =>
  (Array.isArray(list) ? list : []).filter(isJoinedTournamentRegistration);

export function formatRegistrationFee(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `₱${n.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Badge + refund copy for Dashboard / Events registration rows. */
export function tournamentRegistrationStatusMeta(reg) {
  const st = String(reg?.status || '').toLowerCase();
  const refundAmt = formatRegistrationFee(reg?.refund_amount);
  const partnerPending = Boolean(reg?.partner_change_requested);
  const partnerApplied = Boolean(reg?.partner_change_applied_at) && !partnerPending;
  const transferPending = Boolean(reg?.transfer_requested);
  const transferTargetTitle = String(reg?.transfer_target_title || '').trim();
  const canChangePartner = canRequestPartnerChange(reg) && !transferPending;
  const currentPartner = String(reg?.partner_full_name || '').trim();
  const requested = proposedPartnerSummary(reg);
  const previous = previousPartnerName(reg);
  let partnerExtraLabel = '';
  let partnerExtraClass = '';
  let partnerHint = '';
  if (partnerPending) {
    partnerExtraLabel = 'Partner change requested';
    partnerExtraClass = 'bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-200';
    partnerHint = requested
      ? `Waiting for the organizer. Requested partner: ${requested}.`
      : 'Waiting for the organizer to confirm your new partner.';
  } else if (partnerApplied) {
    partnerExtraLabel = 'Partner updated';
    partnerExtraClass = 'bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-200';
    partnerHint = previous && currentPartner
      ? `Was ${previous} · Now ${currentPartner}`
      : (currentPartner ? `Current partner: ${currentPartner}` : 'Your partner was updated.');
  }
  const partnerLine = currentPartner ? `Partner: ${currentPartner}` : '';
  const transferHint = transferPending
    ? (transferTargetTitle
      ? `Waiting for the organizer to move you to ${transferTargetTitle}. Your slot stays here until they confirm.`
      : 'Waiting for the organizer to confirm your transfer. Your slot stays here until they do.')
    : '';
  const partnerBits = {
    canChangePartner,
    partnerChangePending: partnerPending,
    partnerChangeApplied: partnerApplied,
    partnerExtraLabel: transferPending ? 'Transfer requested' : partnerExtraLabel,
    partnerExtraClass: transferPending
      ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-200'
      : partnerExtraClass,
    partnerHint,
    partnerLine,
    canRequestTransfer: canRequestTransfer(reg) && !transferPending,
    transferPending,
    canCancelTransfer: transferPending,
    transferTargetTitle,
  };

  if (st === 'withdrawal_requested') {
    return {
      status: st,
      label: 'Withdrawal requested',
      badgeClass: 'bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-200',
      hint: 'Your slot is still held until the organizer confirms.',
      canWithdraw: false,
      ...partnerBits,
      canChangePartner: false,
      canRequestTransfer: false,
    };
  }
  if (transferPending) {
    return {
      status: st,
      label: 'Transfer requested',
      badgeClass: 'bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-200',
      hint: transferHint,
      canWithdraw: false,
      ...partnerBits,
      canChangePartner: false,
      canRequestTransfer: false,
    };
  }
  if (st === 'withdrawn' || st === 'refunded') {
    return {
      status: st,
      label: reg?.refund_id ? 'Removed · refund recorded' : 'Withdrawn',
      badgeClass: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
      hint: refundAmt
        ? `Refund recorded ${refundAmt}. Collect the payout from the organizer.`
        : 'Collect the payout from the organizer.',
      refundId: reg?.refund_id || '',
      refundAmountLabel: refundAmt,
      canWithdraw: false,
      ...partnerBits,
      canChangePartner: false,
    };
  }
  if (st === 'approved') {
    return {
      status: st,
      label: 'Approved',
      badgeClass: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200',
      hint: partnerHint,
      canWithdraw: Boolean(reg?.can_request_withdraw),
      ...partnerBits,
    };
  }
  return {
    status: st,
    label: reg?.registration_status || 'Pending verification',
    badgeClass: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200',
    hint: partnerHint,
    canWithdraw: Boolean(reg?.can_request_withdraw),
    ...partnerBits,
  };
}
